<template>
  <div v-if="!libraryAssociationResponse">
    <span class="text-caption grey--text text--lighten-1">
      Carefully select each Scan Gauge used in the kit with the corresponding
      gauge in the scan. Use the round-dimples to check that they match.
    </span>
    <GaugeSelection
      v-model="selectedKitGauge"
      :gauges="gauges"
      :libraryAssociation="selectedGaugeLibraries"
      :active="selectedGaugeIndex >= 0"
      :loading="loading"
      @start="scanGaugeSelect(0)"
      @selection="confirmSelection"
      @delete="deleteAssociatedGauge"
    />
    <div class="d-flex">
      <v-btn
        class="mx-2 btn-primary"
        @click="confirmAssociation"
        :disabled="
          !selectedGaugeLibraries.length ||
          selectedGaugeLibraries.includes(null)
        "
        :loading="loading"
      >
        Confirm Association
      </v-btn>
      <v-scale-transition origin="center center">
        <div class="flex-grow-1 ml-3" v-if="loading">
          <v-progress-linear
            rounded
            indeterminate
            color="primary"
            height="36"
            class="rounded-10"
            background-opacity="0.6"
          >
            <span
              class="text-body-2 font-weight-medium white--text loading-pulse"
            >
              PROCESSING...
            </span>
          </v-progress-linear>
        </div>
      </v-scale-transition>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex"
import * as NCFrontend from "@osteon-nexus-connect-lib/nc-frontend"
import GaugeSelection from "./PreAssociation/GaugeSelection.vue"

export default {
  name: "PreAssociation",

  props: ["isActive"],

  components: {
    GaugeSelection
  },

  data() {
    return {
      loading: false,
      selectedKitGauge: null,
      gauges: [
        [
          {
            kitGaugeId: 1,
            src: require("@/assets/gauges/0000_3-mm-short-1.png"),
            hemisphereMarkings: 0
          },
          {
            kitGaugeId: 2,
            src: require("@/assets/gauges/0001_3-mm-short-2.png"),
            hemisphereMarkings: 1
          },
          {
            kitGaugeId: 3,
            src: require("@/assets/gauges/0002_3-mm-short-3.png"),
            hemisphereMarkings: 2
          },
          {
            kitGaugeId: 4,
            src: require("@/assets/gauges/0003_3-mm-medium-1.png"),
            hemisphereMarkings: 0
          },
          {
            kitGaugeId: 5,
            src: require("@/assets/gauges/0004_3-mm-medium-2.png"),
            hemisphereMarkings: 1
          },
          {
            kitGaugeId: 6,
            src: require("@/assets/gauges/0005_3-mm-medium-3.png"),
            hemisphereMarkings: 2
          },
          {
            kitGaugeId: 7,
            src: require("@/assets/gauges/0006_3-mm-long-1.png"),
            hemisphereMarkings: 0
          },
          {
            kitGaugeId: 8,
            src: require("@/assets/gauges/0007_3-mm-long-2.png"),
            hemisphereMarkings: 1
          },
          {
            kitGaugeId: 9,
            src: require("@/assets/gauges/0008_3-mm-long-3.png"),
            hemisphereMarkings: 2
          }
        ],
        [
          {
            kitGaugeId: 10,
            src: require("@/assets/gauges/0009_5-mm-short-1.png"),
            hemisphereMarkings: 1
          },
          {
            kitGaugeId: 11,
            src: require("@/assets/gauges/0010_5-mm-short-2.png"),
            hemisphereMarkings: 2
          },
          {
            kitGaugeId: 12,
            src: require("@/assets/gauges/0011_5-mm-short-3.png"),
            hemisphereMarkings: 3
          },
          {
            kitGaugeId: 13,
            src: require("@/assets/gauges/0012_5-mm-medium-1.png"),
            hemisphereMarkings: 1
          },
          {
            kitGaugeId: 14,
            src: require("@/assets/gauges/0013_5-mm-medium-2.png"),
            hemisphereMarkings: 2
          },
          {
            kitGaugeId: 15,
            src: require("@/assets/gauges/0014_5-mm-medium-3.png"),
            hemisphereMarkings: 3
          },
          {
            kitGaugeId: 16,
            src: require("@/assets/gauges/0015_5-mm-long-1.png"),
            hemisphereMarkings: 1
          },
          {
            kitGaugeId: 17,
            src: require("@/assets/gauges/0016_5-mm-long-2.png"),
            hemisphereMarkings: 2
          },
          {
            kitGaugeId: 18,
            src: require("@/assets/gauges/0017_5-mm-long-3.png"),
            hemisphereMarkings: 3
          }
        ]
      ]
    }
  },

  computed: {
    ...mapGetters([
      "validateScansResponse",
      "ncSessionID",
      "kitBoxID",
      "selectedGaugeLibraries",
      "selectedGaugeIndex",
      "libraryAssociationResponse"
    ])
  },

  methods: {
    ...mapActions([
      "setLibraryAssociationEnabled",
      "setSelectedGaugeIndex",
      "setSelectedGaugeLibraries",
      "setLibraryAssociationResponse",
      "setLibraryAssociationViewOptions",
      "setErrorMessage"
    ]),

    scanGaugeSelect(index) {
      if (
        this.selectedGaugeLibraries[this.selectedGaugeIndex] &&
        !this.selectedGaugeLibraries[index]
      )
        this.selectedKitGauge = null

      if (this.selectedGaugeIndex === index) {
        this.selectedKitGauge = null
        this.setSelectedGaugeIndex(-1)
        this.setLibraryAssociationEnabled(true)
        return
      }

      if (this.selectedGaugeLibraries[index]) {
        const gauge = this.gauges
          .flat()
          .find((g) => g.scanBodyCode === this.selectedGaugeLibraries[index])
        this.selectedKitGauge = gauge
      }

      this.setSelectedGaugeIndex(index)
    },

    confirmSelection() {
      let updatedSelectedGaugeLibraries = [...this.selectedGaugeLibraries]
      this.$set(
        updatedSelectedGaugeLibraries,
        this.selectedGaugeIndex,
        this.selectedKitGauge.scanBodyCode
      )
      this.setSelectedGaugeLibraries(updatedSelectedGaugeLibraries)

      this.selectedKitGauge = null

      if (this.selectedGaugeLibraries.every((g) => !!g)) {
        this.setSelectedGaugeIndex(-1)
        this.setLibraryAssociationEnabled(true)
      } else {
        this.setSelectedGaugeIndex(
          this.selectedGaugeLibraries.findIndex((g) => g === null)
        )
      }

      if (this.selectedGaugeLibraries[this.selectedGaugeIndex]) {
        const gauge = this.gauges
          .flat()
          .find(
            (g) =>
              g.scanBodyCode ===
              this.selectedGaugeLibraries[this.selectedGaugeIndex]
          )
        this.selectedKitGauge = gauge
      }
    },

    deleteAssociatedGauge(gaugeId) {
      const idx = this.selectedGaugeLibraries.indexOf(gaugeId)
      let updatedSelectedGaugeLibraries = [...this.selectedGaugeLibraries]
      this.$set(updatedSelectedGaugeLibraries, idx, null)
      this.setSelectedGaugeLibraries(updatedSelectedGaugeLibraries)
    },

    async confirmAssociation() {
      try {
        this.loading = true
        const response = await NCFrontend.ApiClient.sessionAssociateLibraries(
          "/proxy",
          "",
          {
            sessionId: this.ncSessionID,
            kitBoxId: this.kitBoxID.kit_box_id,
            selectedLibraries: this.selectedGaugeLibraries
          }
        )
        this.setLibraryAssociationEnabled(false)
        this.setLibraryAssociationResponse(response)
      } catch (err) {
        console.error("An internal error occurred.")
        throw err
      } finally {
        this.loading = false
      }
    },

    async getNcLibraries() {
      this.loading = true
      this.setSelectedGaugeIndex(-1)
      this.setLibraryAssociationEnabled(false)
      await NCFrontend.ApiClient.gaugeKitGetScanBodyCodes(
        "/proxy",
        "",
        this.kitBoxID.kit_box_id
      )
        .then((gaugeKitResponse) => {
          if (!gaugeKitResponse.scanBodyCodes) {
            this.setErrorMessage("Could not find scan bodies")
            return
          }
          if (gaugeKitResponse.scanBodyCodes.length < 18) {
            const missingCount = 18 - gaugeKitResponse.scanBodyCodes.length
            this.setErrorMessage(
              `Missing ${missingCount} Scan Body Code${
                missingCount > 1 ? "s" : ""
              }`
            )
            return
          }

          this.setSelectedGaugeLibraries(
            new Array(
              this.validateScansResponse.fixedScan.scanGauges.length
            ).fill(null)
          )

          this.gauges = this.gauges.map((cuffHeightSet, setIndex) => {
            const cuffHeight = setIndex === 0 ? "3mm" : "5mm"
            return cuffHeightSet.map((gauge, index) => {
              const offset = cuffHeight === "5mm" ? 9 : 0
              return {
                ...gauge,
                scanBodyCode: gaugeKitResponse.scanBodyCodes[index + offset]
              }
            })
          })

          setTimeout(() => {
            this.setLibraryAssociationEnabled(true)
          }, 0)
        })
        .catch(() => {
          console.error("An internal error occurred.")
          this.setErrorMessage("Could not find scan bodies")
        })
        .finally(() => {
          this.loading = false
        })
    }
  },

  mounted() {
    this.getNcLibraries()
  }
}
</script>
